import { useContext, useCallback } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { object } from 'prop-types'
import { useSelector } from 'react-redux'
import { CvsChipInput, CvsTextField } from '@components'
import { templateCopyContext } from '../context'
import { TextOptionSelector } from './components'
import { FileFormPartial } from './file-form-partial'

const FullWidth = styled.div`
  width: 100%;
`

const FullWidthTextField = styled(CvsTextField)`
  width: 100%;
`

const FullWidthChipInput = styled(CvsChipInput)`
  width: 100%;
`

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const MarginFormControl = styled(FormControlLabel)`
  align-content: left;
`

const nameOptions = [
  {
    value: '{{template_name}}',
    display_name: 'Template Name',
  },
  {
    value: '{{submission_name}}',
    display_name: 'Submission Name',
  },
  {
    value: '{{submitted_at}}',
    display_name: 'Submitted At',
  },
  {
    value: '{{submitted_by}}',
    display_name: 'Submitted By',
  },
]

export const Email = ({ integration, assignment }) => {
  const { onChange } = useContext(templateCopyContext)

  const features = useSelector(state => state.Auth.features)
  const textSelectionOutboundIsEnabled = Boolean(features?.['text-selection-outbound'])

  const onEmailChange = useCallback(
    (value, name) =>
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            [name]: value,
          },
        },
      })),
    [(onChange, assignment)],
  )

  // Stopgap while this gets fixed on backend
  if (assignment.metadata.to && !Array.isArray(assignment.metadata.to)) {
    assignment.metadata.to = JSON.parse(assignment.metadata.to)
  }
  if (assignment.metadata.cc && !Array.isArray(assignment.metadata.cc)) {
    assignment.metadata.cc = JSON.parse(assignment.metadata.cc)
  }
  if (assignment.metadata.bcc && !Array.isArray(assignment.metadata.bcc)) {
    assignment.metadata.bcc = JSON.parse(assignment.metadata.bcc)
  }

  return (
    <FullWidth className="flex flex-col gap-4">
      <FileFormPartial integration={integration} assignment={assignment}>
        <>
          {textSelectionOutboundIsEnabled ? (
            <TextOptionSelector
              assignment={assignment}
              placeholder={t`Subject`}
              label={t`Subject`}
              metadataKey="subject"
              nameOptions={nameOptions}
            />
          ) : (
            <Flex>
              <FullWidthTextField
                placeholder={`Submissions made for ${assignment.name} (Enter)`}
                label={t`Subject`}
                value={assignment.metadata.subject}
                onChange={({ target: { value } }) => onEmailChange(value, 'subject')}
              />
            </Flex>
          )}
          <Flex>
            <FullWidthChipInput
              placeholder={t`example@claravine.com (Enter)`}
              label={t`To`}
              value={assignment.metadata.to}
              onChange={val => onEmailChange(val, 'to')}
            />
          </Flex>
          <Flex>
            <FullWidthChipInput
              placeholder={t`example@claravine.com (Enter)`}
              label={t`cc`}
              value={assignment.metadata.cc}
              onChange={val => onEmailChange(val, 'cc')}
              style={{ marginRight: 8 }}
            />
            <FullWidthChipInput
              placeholder={t`example@claravine.com (Enter)`}
              label={t`bcc`}
              value={assignment.metadata.bcc}
              onChange={val => onEmailChange(val, 'bcc')}
            />
          </Flex>
          <Flex>
            <MarginFormControl
              size="small"
              control={
                <Checkbox
                  checked={Boolean(+assignment.metadata.sendToSubmitter)}
                  onClick={({ target: { checked } }) =>
                    onEmailChange(checked ? 1 : 0, 'sendToSubmitter')
                  }
                />
              }
              label={t`Send to submitter`}
            />
          </Flex>
        </>
      </FileFormPartial>
    </FullWidth>
  )
}

Email.propTypes = {
  assignment: object.isRequired,
  integration: object.isRequired,
}
